import React from 'react'
import NextImage from 'next/image'

import { Icon } from '@wartek-id/icon'
import { Shimmer } from '@wartek-id/shimmer'
import { Text } from '@wartek-id/text-v1'

import { DocumentListItem } from './CategoryListItem/DocumentListItem'
import DocumentPreviewPanel from './DocumentPreviewPanel'
import PageContainer from './PageContainer'
import KMDisclaimer from './KMDisclaimer'

import type { ResponseSuccessGetDocumentCategoryDetail } from 'api/document_collection/useAPIDocumentCollection'
import type { TContentSearchResult } from 'types/content-search'

interface DesktopSplitViewLayoutProps {
  isLoading: boolean
  categoryInfo: ResponseSuccessGetDocumentCategoryDetail['data']
  desktopHeader: React.ReactNode
  selectedDocumentId: string | null
  onSelectDocument: (doc: TContentSearchResult) => void
  onGuideClick: () => void
  Link: React.ComponentType<{
    isExternalLink: boolean
    href: string
    target: string
    onClick?: () => void
    children: React.ReactNode
  }>
  CategoryInfoSection: React.ComponentType<{}>
}

export const DesktopSplitViewLayout = ({
  isLoading,
  categoryInfo,
  desktopHeader,
  selectedDocumentId,
  onSelectDocument,
  onGuideClick,
  Link,
  CategoryInfoSection,
}: DesktopSplitViewLayoutProps) => {
  return (
    <div className="flex flex-col h-screen">
      {desktopHeader}
      <div className="flex-1 min-h-0 overflow-hidden bg-surface-subdued">
        <PageContainer className="flex h-full !py-0">
          {/* Left panel: category info + document list */}
          <aside
            id="desktop-split-left-panel"
            className="w-[38%] min-w-0 border-r border-gray-200 overflow-y-auto flex flex-col"
          >
            <div className="bg-white">
              {isLoading ? (
                <div className="flex flex-col p-4 md:px-8">
                  <div className="flex flex-row items-center justify-between">
                    <div className="mt-2 flex flex-col !w-full">
                      <Shimmer
                        height={15}
                        className="mb-4 !w-full"
                        style={{ borderRadius: 12 }}
                      />
                      <Shimmer
                        height={10}
                        className="mb-4 !w-full"
                        style={{ borderRadius: 12 }}
                      />
                      <Shimmer
                        height={10}
                        className="mb-4 !w-full"
                        style={{ borderRadius: 12 }}
                      />
                    </div>
                    <Shimmer
                      height={80}
                      className="ml-4 !w-full max-w-[25vw]"
                      style={{ borderRadius: 12 }}
                    />
                  </div>
                </div>
              ) : (
                <>
                  <CategoryInfoSection />
                  {categoryInfo?.guideUrl && (
                    <div className="px-4 md:px-8 mb-4">
                      <Link
                        href={categoryInfo?.guideUrl}
                        isExternalLink={true}
                        target="_blank"
                        onClick={onGuideClick}
                      >
                        <div className="flex flex-row justify-between p-4 mt-4 space-x-4 border rounded-lg">
                          <div className="flex-none w-8 h-8">
                            <NextImage
                              src={categoryInfo?.guideThumbnailUrl}
                              alt="Guide"
                              width="32px"
                              height="32px"
                              className="object-cover object-center"
                              layout="responsive"
                            />
                          </div>
                          <div className="grow flex flex-col ml-4 space-y-[2px]">
                            <Text variant="action" className="line-clamp-3">
                              {categoryInfo?.guideTitle}
                            </Text>
                            <Text
                              variant="body-sm"
                              className="line-clamp-3"
                              color="subdued"
                            >
                              {categoryInfo?.guideContent}
                            </Text>
                          </div>
                          <div className="flex-none">
                            <Icon className="!text-subdued">chevron_right</Icon>
                          </div>
                        </div>
                      </Link>
                    </div>
                  )}
                  {categoryInfo?.categoryType !== 'CT_UNDEFINED' && (
                    <KMDisclaimer className="mb-3 px-4" />
                  )}
                </>
              )}
            </div>
            <div className="flex-1 bg-white">
              <DocumentListItem
                parentData={categoryInfo}
                selectedId={selectedDocumentId}
                onSelectItem={onSelectDocument}
                scrollableTarget="desktop-split-left-panel"
              />
            </div>
          </aside>

          {/* Right panel: document preview */}
          <main className="flex-1 min-w-0 overflow-y-auto bg-white">
            {selectedDocumentId ? (
              <DocumentPreviewPanel
                id={selectedDocumentId}
                parentCategory={categoryInfo?.parentName}
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                <Text variant="body" color="subdued">
                  Pilih dokumen untuk melihat pratinjau
                </Text>
              </div>
            )}
          </main>
        </PageContainer>
      </div>
    </div>
  )
}
